import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../firebase';
import {
  GET_DPP_MESSAGES,
  GET_CROSS_POSTED_PROMPTS,
  GET_OTHER_DPP_SUBMISSIONS,
  DELETE_OTHER_DPP_SUBMISSION
} from './types';
import { retreievePlainTextAfterStorage } from '../utils/convertEditorState';
import genderToAbbreviation from '../utils/genderToAbbreviation';
import { openSnackbar } from './snackbarActions';

// The Reddit (dpp) operations that need bot credentials now run in admin-gated
// Cloud Functions (see functions/dppCallableFunctions.js) — the clientId /
// clientSecret / refreshToken are NEVER sent to the browser. These callables do
// only the Reddit call; the Firestore bookkeeping stays here.
const callSendDppMessage = httpsCallable(functions, 'sendDppMessage');
const callReplyDppMessage = httpsCallable(functions, 'replyDppMessage');
const callCrossPostPrompt = httpsCallable(functions, 'crossPostPrompt');

// delete the specific doc from otherDppSubmissions collection
// note that this doesn't affect the otherDPP, it's just for our own firestore
export const deleteOtherDppSubmission = submissionId => async dispatch => {
  const otherDppSubmissionRef = doc(
    firestore,
    'otherDppSubmissions',
    submissionId
  );

  await deleteDoc(otherDppSubmissionRef);
  dispatch({
    type: DELETE_OTHER_DPP_SUBMISSION,
    payload: { submissionId }
  });
};

// send a message to a DPP user, used by AdminOtherDppSubmissions.js component
// the actual Reddit compose happens server-side; here we record that DPP user
// and delete the otherDppSubmission from the collection and redux state
export const sendDppMessage = ({
  messageReceiver,
  replyMessage,
  submissionId
}) => async dispatch => {
  try {
    await callSendDppMessage({ messageReceiver, replyMessage });

    const messagedSubPostersRef = doc(firestore, 'misc', 'messagedSubPosters');
    await setDoc(
      messagedSubPostersRef,
      {
        [messageReceiver.toLowerCase()]: {
          username: messageReceiver,
          count: increment(1),
          lastMessaged: serverTimestamp()
        }
      },
      { merge: true }
    );
    if (submissionId) {
      await dispatch(deleteOtherDppSubmission(submissionId));
    }
    dispatch(
      openSnackbar('SuccessSnackbar', {
        message: `Successfully messaged ${messageReceiver}`
      })
    );
  } catch (error) {
    console.error({ error });
    dispatch(
      openSnackbar('ErrorSnackbar', {
        message: 'Could not send the message. Please try again.'
      })
    );
  }
};

// get all of the docs from otherDppSubmissions, which are submissions posted on
// the other DPP (official / most popular one)
export const getOtherDppSubmissions = () => async dispatch => {
  try {
    const otherDppSubmissionsRef = collection(firestore, 'otherDppSubmissions');
    const otherDppSubmissionsSnapshot = await getDocs(otherDppSubmissionsRef);
    const otherDppSubmissionsDocs = otherDppSubmissionsSnapshot.docs.map(
      otherDppSubmissionSnapshot => {
        const otherDppSubmissionsDoc = otherDppSubmissionSnapshot.data();
        return otherDppSubmissionsDoc;
      }
    );

    // get the usernames of all of the users (posters) that we've messaged previously
    const messagedSubPostersRef = doc(firestore, 'misc', 'messagedSubPosters');
    const messagedSubPostersSnapshot = await getDoc(messagedSubPostersRef);
    const messagedSubPosters = messagedSubPostersSnapshot.data() || {};

    // modify submissions based on user info from messagedSubPosters
    const submissions = otherDppSubmissionsDocs.map(otherDppSubmissionsDoc => {
      const { author } = otherDppSubmissionsDoc;
      const authorLower = author.toLowerCase();
      let previousMessagesToAuthorCount;
      let lastMessagedAuthor;
      if (messagedSubPosters[authorLower]) {
        const { count, lastMessaged } = messagedSubPosters[authorLower];
        previousMessagesToAuthorCount = count;
        lastMessagedAuthor = lastMessaged;
      }
      return {
        ...otherDppSubmissionsDoc,
        previousMessagesToAuthorCount,
        lastMessagedAuthor
      };
    });
    dispatch({
      type: GET_OTHER_DPP_SUBMISSIONS,
      payload: { submissions }
    });
  } catch (error) {
    console.error({ error });
  }
};

// get the ids of posts that have already been posted to DPP
export const getCrossPostedPromptsIds = () => async dispatch => {
  try {
    const crossPostedPromptsRef = doc(firestore, 'misc', 'crossPostedPrompts');
    const crossPostedPromptsSnapshot = await getDoc(crossPostedPromptsRef);
    const crossPostedPromptsIds = crossPostedPromptsSnapshot.data();
    dispatch({
      type: GET_CROSS_POSTED_PROMPTS,
      payload: { crossPostedPromptsIds }
    });
  } catch (error) {
    console.error({ error });
  }
};

// get all of the messages from dppMessagesForUsers
export const getAllDppMessages = () => async dispatch => {
  // get all of the repliedToDppUsers to see which senders of messages we've
  // already replied to and the number of message we've sent to them previously
  const repliedToDppUsersRef = doc(firestore, 'misc', 'repliedToDppUsers');
  const repliedToDppUsersDoc = await getDoc(repliedToDppUsersRef);
  let repliedToDppUsers = repliedToDppUsersDoc.data();
  if (!repliedToDppUsers) {
    repliedToDppUsers = {};
  }

  const dppMessagesRef = collectionGroup(firestore, 'dppMessages');
  const dppMessagesSnapshot = await getDocs(dppMessagesRef);
  const dppMessagesDocs = dppMessagesSnapshot.docs;
  const dppMessages = dppMessagesDocs.map(dppMessageDoc => {
    const dppMessageData = dppMessageDoc.data();
    const dppMessageSender = dppMessageData.sender;
    // number of messages we've sent to the message sender previously
    const numberOfRepliesSentToMsgSender =
      repliedToDppUsers[dppMessageSender] || 0;

    return { ...dppMessageData, numberOfRepliesSentToMsgSender };
  });
  dispatch({
    type: GET_DPP_MESSAGES,
    payload: {
      dppMessages
    }
  });
};

// reply to the dpp message the user has received (Reddit reply runs server-side)
export const replyDppMessage = (
  dppReceiver,
  dppMessageId,
  dppMessageSender,
  dppMessageReplyText
) => async dispatch => {
  try {
    await callReplyDppMessage({
      dppReceiver,
      dppMessageId,
      dppMessageReplyText
    });

    // make a record that we've replied to that user before, increase count by 1
    const repliedToDppUsersRef = doc(firestore, 'misc', 'repliedToDppUsers');
    await setDoc(
      repliedToDppUsersRef,
      { [dppMessageSender]: increment(1) },
      { merge: true }
    );

    dispatch(
      openSnackbar('SuccessSnackbar', {
        message: 'Successfully replied!'
      })
    );
    return true;
  } catch (error) {
    console.error({ error });
    dispatch(
      openSnackbar('ErrorSnackbar', {
        message: 'Could not send the reply. Please try again.'
      })
    );
    return null;
  }
};

// deletes the dppMessage doc in firestore
// Does Not Delete it in DPP
export const deleteDppMessageInFirestore = (
  dppUsername,
  dppMessageId
) => async dispatch => {
  try {
    const dppMessageRef = doc(
      firestore,
      'dppMessagesForUsers',
      dppUsername,
      'dppMessages',
      dppMessageId
    );
    await deleteDoc(dppMessageRef);
    dispatch(
      openSnackbar('SuccessSnackbar', {
        message: `Successfully deleted the message ${dppMessageId}`
      })
    );
  } catch (error) {
    console.error({ error });
    dispatch(
      openSnackbar('ErrorSnackbar', {
        message: 'Could not delete the message. Please try again.'
      })
    );
  }
};

// posts the prompt to DPP (Reddit selfpost runs server-side)
export const crossPostPrompt = promptId => async dispatch => {
  try {
    const promptRef = doc(firestore, 'prompts', promptId);
    const promptSnapshot = await getDoc(promptRef);
    const promptData = promptSnapshot.data();
    const {
      title: preTitle,
      text: preprocessedText,
      iam,
      lookingfor
    } = promptData;
    const text = retreievePlainTextAfterStorage(preprocessedText);
    const iamAbbreviation = genderToAbbreviation(iam);
    const lookingforAbbreviation = genderToAbbreviation(lookingfor);
    const title = `[${iamAbbreviation}4${lookingforAbbreviation}] ${preTitle}`;
    const iamLookingforKey = `${iamAbbreviation.toLowerCase()}4${lookingforAbbreviation.toLowerCase()}`;

    await callCrossPostPrompt({ title, text, iamLookingforKey });

    // add the id of the cross posted prompt into the doc with all of the
    // cross posted prompts
    const crossPostedPromptsRef = doc(firestore, 'misc', 'crossPostedPrompts');
    await updateDoc(crossPostedPromptsRef, { [promptId]: promptId });

    dispatch(
      openSnackbar('SuccessSnackbar', {
        message: 'Successfully cross-posted!'
      })
    );
  } catch (error) {
    console.error({ error });
    dispatch(
      openSnackbar('ErrorSnackbar', {
        message: 'Could not cross-post. Please try again.'
      })
    );
  }
};
